import { EntitiesApiUrl, get, post, put } from './ExternalApi';

// Types aligned with backend DTO (camelCase)
export interface ChatLastMessagePreviewDto {
  id: string;
  type: string; // text|image|location|note
  textSnippet?: string | null;
  from: string; // user|agent|driver|system
  at: string; // ISO
}

export interface ChatParticipantLiteDto {
  role: string;
  displayName?: string | null;
  // Operator-only attribution fields (never sent to the customer widget):
  participantId?: string | null; // join key for a message's fromParticipantId
  userId?: string | null;        // the person's user id — used to tell "me" from a teammate
  websiteId?: string | null;     // the company this participant acts for
  company?: string | null;       // that company's display name (e.g. "KEMER VIP TURIZM")
}

export interface ChatAssignedAgentDto {
  agentId: string;
  displayName?: string | null;
}

export interface ChatConversationListItemDto {
  conversationId: string; // GUID
  status: 'open' | 'resolved';
  websiteId?: string | null;
  bookingGuid?: string | null;
  customerId?: number | null;
  target: string;
  lastMessage?: ChatLastMessagePreviewDto | null;
  unreadCount: number; // Unread count for current user (from JWT token)
  assignedAgent?: ChatAssignedAgentDto | null;
  participants: ChatParticipantLiteDto[];
  priority?: string | null;
  labels?: string[] | null;
  updatedAt?: string | null; // ISO
  isAssignedToMe?: boolean; // Whether conversation is assigned to current user
  // Booking metadata snapshot
  bookingCustomerId?: string | null;
  bookingPickupTimeUtc?: string | null; // ISO UTC
  bookingIanaTimezone?: string | null; // e.g. "America/New_York"
  bookingMetadataUpdatedAt?: string | null; // ISO
}

export interface ChatConversationListResponseDto {
  items: ChatConversationListItemDto[];
  page: number;
  pageSize: number;
  total?: number | null;
  nextPage?: number | null;
}

export interface ConversationSummaryResponse {
  websiteId: string;
  open: { all: number; my: number; unassigned: number };
  resolved: { last24h: number; total: number };
  unread: { my: number; total: number };
  // Team-wide "customer is waiting for a reply" signal (last message from the customer).
  // waiting = any age; unanswered = waiting ≥ 2 min → escalated to everyone so anyone can pick it up.
  attention: { waiting: number; unanswered: number };
  // Normalized aliases. The API returns `unread: { my, total }`; these mirror it so existing
  // callers that read totalUnreadCount/myUnreadCount keep working.
  totalUnreadCount: number; // = unread.total (assigned conversations with unread, website-wide)
  myUnreadCount: number;    // = unread.my (conversations assigned to me with unread)
  // Null means the backend did not provide a valid notification count.
  notification: number | null;
  // New notification summary shape returned by the backend.
  notifications: { unreadCount: number | null };
}

export interface FetchConversationsParams {
  websiteId: string;
  status?: 'open' | 'resolved';
  scope?: 'all' | 'my' | 'unassigned';
  agentId?: string; // required for scope=my
  target?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

function buildQuery(params: Record<string, any>): string {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    usp.append(k, String(v));
  });
  return usp.toString();
}

/**
 * Fetch conversations with unread counts and assignment status.
 * NOTE: User ID is automatically extracted from JWT token by backend.
 * DO NOT pass userId as a parameter - it will be ignored for security.
 */
export async function fetchConversations(params: FetchConversationsParams): Promise<ChatConversationListResponseDto> {
  const query = buildQuery(params);
  const url = `${EntitiesApiUrl}/api/chat/conversations${query ? `?${query}` : ''}`;
  const result = await get<ChatConversationListResponseDto>(url);
  const normalized = result ?? { items: [], page: params.page ?? 1, pageSize: params.pageSize ?? 50, total: 0, nextPage: null };
  // Normalize status to lowercase (C# enum serializes as Pascal case: "Open"/"Resolved")
  normalized.items = normalized.items.map(item => ({
    ...item,
    status: (item.status as string).toLowerCase() as 'open' | 'resolved',
  }));
  return normalized;
}

export interface BookingByPhoneDto {
  bookingId: string;
  reference?: string | null;
  driverOnRouteStatus?: string | null;
  passengerName?: string | null;
  passengerPhone?: string | null;
  passengerEmail?: string | null;
  pickupUtc: string;
  pickupLocal: string;
  ianaTimezoneId?: string | null;
  from?: string | null;
  to?: string | null;
  fromAddress?: string | null;
  toAddress?: string | null;
  durationInMinutes?: number | null;
  distanceInKilometers?: number | null;
  driverName?: string | null;
  driverPhone?: string | null;
  car?: string | null;
  carPlate?: string | null;
}

export interface BookingLinkSmsPreviewDto {
  body: string;
  link?: string | null;
  /** False when the website has no SMS provider — show why rather than offering a Send that will fail. */
  smsConfigured: boolean;
  cooldownActive: boolean;
  cooldownMinutesRemaining?: number | null;
  phone?: string | null;
}

/**
 * The text we WOULD send, so the operator can read and edit it before committing to a billable message.
 * Also reports whether the cooldown is holding — advisory only, since the authoritative check runs again
 * at send time (a preview left open for ten minutes would otherwise report a stale answer).
 */
export async function previewBookingLinkSms(websiteId: string, bookingId: string): Promise<BookingLinkSmsPreviewDto> {
  const url = `${EntitiesApiUrl}/api/chat/bookings/${bookingId}/link-sms-preview?websiteId=${encodeURIComponent(websiteId)}`;
  const raw: any = (await get(url)) ?? {};
  return {
    body: raw.body ?? '',
    link: raw.link ?? null,
    smsConfigured: raw.smsConfigured !== false,
    cooldownActive: !!raw.cooldownActive,
    cooldownMinutesRemaining: raw.cooldownMinutesRemaining ?? null,
    phone: raw.phone ?? null,
  };
}

/**
 * Bookings a phone number could belong to — backs the "+" (open a conversation by phone) dialog.
 *
 * Same matching rule the IVR and the inbound-SMS bridge use (last 9 digits, pickup no older than the
 * website's past-booking window, nearest first), so a number resolves identically however the customer
 * reaches us. Returns ALL matches: an outbound plus a return on one number is the dispatcher's choice.
 *
 * `tooShort` is returned instead of results when fewer than 6 digits were supplied — a suffix match on
 * three digits would return half the database.
 */
/**
 * Look a booking up by phone OR by exact reference.
 *
 * Reference search exists for D8 (dispatcher-initiated contact): OTA traveller numbers are often masked
 * proxies, so phone search misses precisely the bookings that feature is for. It also ignores the
 * pickup-time window — typing an exact reference means that booking, however old.
 */
export async function findBookingsByPhone(
  websiteId: string, phone: string, reference?: string,
): Promise<{ items: BookingByPhoneDto[]; tooShort: boolean }> {
  const query = buildQuery(reference ? { websiteId, reference } : { websiteId, phone });
  const url = `${EntitiesApiUrl}/api/chat/bookings/by-phone?${query}`;
  const raw: any = (await get(url)) ?? {};
  return { items: raw?.items ?? [], tooShort: !!raw?.tooShort };
}

/**
 * Find-or-create the support conversation for a booking and return its id.
 *
 * Posts NOTHING into the thread. A synthetic "customer" or "system" line would register as unanswered
 * (see ChatService.GetConversationSummaryAsync / ChatEscalationService) and page the on-duty operators
 * about a message no customer ever sent. The dispatcher's first typed reply is what starts the thread.
 */
export async function openConversationForBooking(websiteId: string, bookingId: string): Promise<string> {
  const url = `${EntitiesApiUrl}/api/chat/session`;
  const raw: any = await post(url, { target: 'support', bookingId, websiteId });
  const conversationId = raw?.data?.conversationId ?? raw?.result?.conversationId ?? raw?.conversationId;
  if (!conversationId) throw new Error('Could not open a conversation for this booking');
  return conversationId;
}

/**
 * Text the customer the link to manage their booking, stating that we do not take support requests by SMS.
 *
 * Same sender as the inbound-SMS bridge's acknowledgement (BookingLinkSmsService), so the wording, the
 * shortened link and the ledger entry are identical however the send was triggered. The disclaimer matters:
 * this SMS leaves through the WEBSITE's Twilio/Plivo sender, not the number the customer dialled or the
 * operator handset they were texting, so a reply to it may reach a number nobody is watching.
 */
export async function sendBookingLinkSms(
  websiteId: string, bookingId: string, phone?: string, conversationId?: string, body?: string,
): Promise<{ sent: boolean; skipReason?: string | null; body?: string | null }> {
  const url = `${EntitiesApiUrl}/api/chat/bookings/send-link`;
  const raw: any = await post(url, { websiteId, bookingId, phone, conversationId, body });
  const data = raw?.data ?? raw?.result ?? raw ?? {};
  return { sent: !!data.sent, skipReason: data.skipReason ?? null, body: data.body ?? null };
}

/**
 * Secured, viewer-scoped booking context for the info pane. Returns the caller's OWN booking
 * (a supplier gets its own mirror booking, never the provider's record) with operational fields
 * ONLY — no pricing. This replaces the anonymous customer-page booking fetch on the support surface.
 */
export async function getBookingContext(conversationId: string, websiteId?: string): Promise<any | null> {
  // Pass the website the operator is viewing from so a supplier resolves to ITS OWN booking.
  const qs = websiteId ? `?websiteId=${encodeURIComponent(websiteId)}` : '';
  const url = `${EntitiesApiUrl}/api/chat/conversations/${conversationId}/booking-context${qs}`;
  const raw: any = (await get(url)) ?? {};
  return raw?.booking ?? null;
}

/**
 * The dispatcher's own "I'm available for support" state.
 *
 * `isOperator` false means they are on no website's support roster, so the top-bar toggle is hidden rather
 * than shown switched off. Availability spans every website they operate — going off shift is about the
 * person, not one brand.
 */
export async function getSupportAvailability(): Promise<{ isOperator: boolean; available: boolean }> {
  const raw: any = (await get(`${EntitiesApiUrl}/api/chat/support-availability`)) ?? {};
  return { isOperator: !!raw.isOperator, available: !!raw.available };
}

/**
 * Go on or off duty. Also removes the operator from the escalation SMS roster while off, which is the
 * point — someone else is picking the conversations up.
 */
export async function setSupportAvailability(
  available: boolean,
): Promise<{ available: boolean; websitesUpdated: number; unattendedWebsites: string[] }> {
  const raw: any = await put(`${EntitiesApiUrl}/api/chat/support-availability`, { available });
  const data = raw?.data ?? raw?.result ?? raw ?? {};
  return {
    available: !!data.available,
    websitesUpdated: data.websitesUpdated ?? 0,
    // Websites left with nobody on duty by this change — the operator who caused it is the one who can undo it.
    unattendedWebsites: data.unattendedWebsites ?? [],
  };
}

/** How a driver can be reached about a chat message right now. */
export type DriverChatReach = 'app' | 'sms' | 'none';

/**
 * Driver engagement, derived server-side from read state — never from connection presence.
 *
 * `never_joined` is the one to read carefully: the grant is open but the driver has no chat participant at
 * all, so they have never opened the app or the link. It is deliberately NOT `in_chat` — claiming a driver
 * is up to date on no evidence is worse than showing nothing.
 */
export type DriverChatState = 'in_chat' | 'seen_no_reply' | 'not_read' | 'never_joined' | 'none';

export interface DriverInvitePreviewDto {
  canInvite: boolean;
  /** Why not, when `canInvite` is false: no_grant · unreachable · no_sms_provider · no_messages · … */
  reason?: string | null;
  channel?: 'push' | 'sms' | null;
  /** Push notification title (carries the booking reference). Null on the SMS path, which has no title. */
  title?: string | null;
  /** The composed message as it will be sent, including the SMS frame and link. */
  body?: string | null;
  /** Just the message part — what the dispatcher may replace with their own words. */
  message?: string | null;
  /** Cap on `message`: what fits beside the link in one SMS segment, or the push ceiling. */
  maxMessageChars?: number | null;
  phone?: string | null;
  lastInviteAtUtc?: string | null;
}

/** What an invite would do right now. Sends nothing. */
export async function previewDriverInvite(conversationId: string, websiteId?: string): Promise<DriverInvitePreviewDto> {
  const qs = websiteId ? `?websiteId=${encodeURIComponent(websiteId)}` : '';
  const url = `${EntitiesApiUrl}/api/chat/conversations/${conversationId}/invite-driver/preview${qs}`;
  const raw: any = (await get(url)) ?? {};
  return {
    canInvite: !!raw.canInvite,
    reason: raw.reason ?? null,
    channel: raw.channel ?? null,
    title: raw.title ?? null,
    body: raw.body ?? null,
    message: raw.message ?? null,
    maxMessageChars: raw.maxMessageChars ?? null,
    phone: raw.phone ?? null,
    lastInviteAtUtc: raw.lastInviteAtUtc ?? null,
  };
}

/**
 * Pull the driver into this conversation — one push, or one SMS with the driver booking link when they
 * have no app. Works whether or not the website has the automatic driver-alert ladder switched on: this is
 * a dispatcher deciding, which is exactly what that switch defers to automation.
 */
export async function sendDriverInvite(
  conversationId: string, websiteId?: string, message?: string,
): Promise<{ sent: boolean; channel?: string | null; reason?: string | null }> {
  const qs = websiteId ? `?websiteId=${encodeURIComponent(websiteId)}` : '';
  const url = `${EntitiesApiUrl}/api/chat/conversations/${conversationId}/invite-driver${qs}`;
  // Blank means "send the default" — an untouched dialog and no dialog produce the same message.
  const raw: any = await post(url, { message: message?.trim() || null });
  const data = raw?.data ?? raw?.result ?? raw ?? {};
  return { sent: !!data.sent, channel: data.channel ?? null, reason: data.reason ?? null };
}

/**
 * Pull this conversation's history from Booking.com and insert anything we are missing.
 *
 * Exists because that is how a lost message is actually discovered: a traveller says "I messaged you and
 * heard nothing". The thread otherwise just looks like a customer who went quiet, with nothing to act on.
 * Returns how many messages were recovered — 0 is the normal, healthy answer.
 */
export async function syncExternalHistory(conversationId: string): Promise<{ recovered: number }> {
  const url = `${EntitiesApiUrl}/api/chat/conversations/${conversationId}/sync-external`;
  const raw: any = await post(url, {});
  const data = raw?.data ?? raw?.result ?? raw ?? {};
  return { recovered: data.recovered ?? 0 };
}

/**
 * Fetch conversation summary with counts.
 * NOTE: User ID is automatically extracted from JWT token by backend.
 * DO NOT pass userId/agentId - unread counts are calculated for authenticated user.
 */
export async function fetchConversationSummary(websiteId: string): Promise<ConversationSummaryResponse> {
  const query = buildQuery({ websiteId });
  const url = `${EntitiesApiUrl}/api/chat/conversations/summary?${query}`;
  // The backend returns { websiteId, open, resolved, unread:{my,total} }. Normalize the unread
  // counts to the flat aliases callers use (the navbar badge reads totalUnreadCount).
  const raw: any = (await get(url)) ?? {};
  const unread = raw.unread ?? {};
  const attention = raw.attention ?? {};
  const my = unread.my ?? raw.myUnreadCount ?? 0;
  const total = unread.total ?? raw.totalUnreadCount ?? 0;
  const nestedNotificationCount = raw.notifications?.unreadCount;
  const notification = typeof nestedNotificationCount === 'number' && Number.isFinite(nestedNotificationCount) && nestedNotificationCount >= 0
    ? nestedNotificationCount
    : typeof raw.notification === 'number' && Number.isFinite(raw.notification) && raw.notification >= 0
      ? raw.notification
      : null;
  return {
    websiteId: raw.websiteId ?? websiteId,
    open: raw.open ?? { all: 0, my: 0, unassigned: 0 },
    resolved: raw.resolved ?? { last24h: 0, total: 0 },
    unread: { my, total },
    attention: { waiting: attention.waiting ?? 0, unanswered: attention.unanswered ?? 0 },
    myUnreadCount: my,
    totalUnreadCount: total,
    notification,
    notifications: { unreadCount: notification },
  };
}

export interface ConversationStatusResponse {
  success: boolean;
  changed: boolean;
  status: 'open' | 'resolved';
}

// `post` returns the ApiResponse envelope; unwrap to the actual status payload.
function unwrapStatus(raw: any): ConversationStatusResponse {
  return (raw?.data ?? raw?.result ?? raw) as ConversationStatusResponse;
}

export async function resolveConversation(conversationId: string): Promise<ConversationStatusResponse> {
  const url = `${EntitiesApiUrl}/api/chat/conversations/${conversationId}/resolve`;
  return unwrapStatus(await post(url, {}));
}

export async function reopenConversation(conversationId: string): Promise<ConversationStatusResponse> {
  const url = `${EntitiesApiUrl}/api/chat/conversations/${conversationId}/reopen`;
  return unwrapStatus(await post(url, {}));
}

export interface AssignConversationResponse {
  success: boolean;
  assignedToUserId: string;
  assignedAgentName?: string | null;
}

/** Explicitly claim a conversation for the current operator. displayName is shown to other operators. */
export async function assignConversation(conversationId: string, displayName?: string): Promise<AssignConversationResponse> {
  const url = `${EntitiesApiUrl}/api/chat/conversations/${conversationId}/assign`;
  const raw: any = await post(url, { displayName });
  return (raw?.data ?? raw?.result ?? raw) as AssignConversationResponse;
}
